import { useState } from "react";
import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Textarea } from "@component/textarea";
import { AlertCircleIcon, LoaderIcon, RotateCcwIcon } from "lucide-react";
import { updateInterviewStatus } from "@service/interview";
import { interviewStatusPendingInterview, retakeReasons } from "@constant/app";
import { trackEvent } from "@util/mixpanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Label } from "./label";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (reason: string) => void;
}

export default function RetakeRequestDialogV2({
    isOpen,
    onClose,
    onSubmit,
}: Props) {
    const [selectedReason, setSelectedReason] = useState<string>("");
    const [reason, setReason] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleSubmit = () => {
        setIsSubmitting(true);
        updateInterviewStatus({
            "new_status": interviewStatusPendingInterview,
            "retake_reason": selectedReason === "other" ? reason : selectedReason
        }).then((res) => {
            console.log("Updated status from retake", res);
            trackEvent("interview_retake", {
                "reason": selectedReason === "other" ? reason : selectedReason
            })
        }).then(() => {
            onSubmit(reason);
            setIsSubmitting(false);
            setReason("");
        })
    };

    const getSelectedReasonLabel = () => {
        const reason = retakeReasons.find((r) => r.value === selectedReason);
        return reason ? reason.label : "";
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md p-4">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-left flex items-center gap-2">
                        <AlertCircleIcon className="h-5 w-5 text-blue-600" />
                        Retake your interview
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="text-sm text-gray-600 px-1 space-y-2">
                        <p>
                            Select the interview you'd like to retake. This will also reset your application progress.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600 px-1">
                            Why would you like to retake this interview
                        </Label>
                        <Select value={selectedReason} onValueChange={setSelectedReason}>
                            <SelectTrigger className="w-full py-3 px-4">
                                <SelectValue placeholder="Select a reason">
                                    {selectedReason
                                        ? getSelectedReasonLabel()
                                        : "Select a reason"}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {retakeReasons.map((reason) => (
                                    <SelectItem key={reason.value} value={reason.value}>
                                        <div className="flex flex-col items-start">
                                            <span className="font-medium text-gray-900">
                                                {reason.label}
                                            </span>
                                            <span className="text-xs text-gray-500 mt-0.5">
                                                {reason.description}
                                            </span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedReason === "other" && (
                        <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-900 px-1">
                                Please provide more details
                            </label>
                            <Textarea
                                placeholder="Type your message here..."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="min-h-[100px] resize-none p-4"
                            />
                        </div>
                    )}

                    <Button
                        onClick={handleSubmit}
                        disabled={selectedReason === "" || isSubmitting}
                        className={`w-full py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white`}
                    >
                        {isSubmitting ? (
                            <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <RotateCcwIcon className="h-4 w-4 mr-2" />
                        )}
                        {isSubmitting ? "Resetting interview..." : "Retake Interview"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
